import React, { useEffect, useRef, useState } from 'react';
import { BriefcaseConveyorBelt, Luggage, Ticket, TicketCheck, TicketX } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/**
 * Baggage and fare-terms icons for a flight card (see PLAN-fare-terms.md).
 *
 * The card shows only small icons; details open on hover, focus + Enter, or
 * tap on touch screens. Unknown values render nothing, so a flight with no
 * fare terms looks exactly as it did before.
 */

// Leg terms override the component default when a leg differs.
export const resolveFareTerms = (leg, componentData) => leg?.fare_terms || componentData?.fare_terms || null;

const isKnown = (value) => value !== null && value !== undefined;

const formatFee = (amount, currency) => {
  if (!isKnown(amount) || amount === '') return '';
  const value = Number(amount);
  if (!Number.isFinite(value)) return String(amount);
  if (value === 0) return 'no';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(value);
  } catch {
    return `${amount} ${currency || ''}`.trim();
  }
};

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const SOURCE_LABELS = {
  duffel: 'From booking',
  parsed: 'From confirmation',
  manual: 'Entered manually',
  typical: 'Typical allowance, verify with the airline',
};

const sourceLine = (terms) => {
  const label = terms.source_detail || SOURCE_LABELS[terms.source] || '';
  const date = terms.source === 'typical' ? '' : formatDate(terms.captured_at);
  return [label, date].filter(Boolean).join(' · ');
};

const bagSummary = (bag, noun) => {
  if (!bag || !isKnown(bag.quantity)) return null;
  if (bag.quantity === 0) return `No ${noun} included`;
  const weight = isKnown(bag.weight_kg) ? ` × ${bag.weight_kg} kg` : '';
  return `${bag.quantity} ${noun}${bag.quantity === 1 ? '' : 's'}${weight}`;
};

const CANCEL_OUTCOMES = {
  cash_refund: 'Refund',
  travel_credit: 'Travel credit only',
  none: 'No refund or credit',
};

const changeSummary = (change) => {
  if (!change || !isKnown(change.allowed)) return null;
  if (!change.allowed) return 'Not allowed';
  const fee = formatFee(change.fee_amount, change.fee_currency);
  return fee ? `Allowed, ${fee} fee` : 'Allowed';
};

const cancelSummary = (cancel) => {
  if (!cancel) return null;
  const base = CANCEL_OUTCOMES[cancel.outcome]
    || (isKnown(cancel.allowed) ? (cancel.allowed ? 'Refundable' : 'Non-refundable') : null);
  if (!base) return null;
  const fee = cancel.allowed ? formatFee(cancel.fee_amount, cancel.fee_currency) : '';
  return fee ? `${base}, ${fee} fee` : base;
};

const ticketState = (terms) => {
  const change = terms.change?.allowed;
  const cancel = terms.cancel?.allowed;
  if (change === true && cancel === true) return 'flexible';
  if (change === false && cancel === false) return 'locked';
  if (isKnown(change) || isKnown(cancel) || terms.fare_brand) return 'mixed';
  return null;
};

const TICKET_ICONS = { flexible: TicketCheck, mixed: Ticket, locked: TicketX };

const HoverDetail = ({ label, title, rows, footer, iconClassName, children }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Mouse hover opens; touch and keyboard fall through to Radix's click toggle.
  const onEnter = (e) => {
    if (e.pointerType !== 'mouse') return;
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const onLeave = (e) => {
    if (e.pointerType !== 'mouse') return;
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          onPointerEnter={onEnter}
          onPointerLeave={onLeave}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-1 text-xs font-medium hover:bg-ui-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-blue-line ${iconClassName}`}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={onEnter}
        onPointerLeave={onLeave}
        onClick={(e) => e.stopPropagation()}
        className="w-64 p-3 text-xs bg-ui-surface border-ui-line text-ui-ink"
      >
        <div className="mb-1.5 font-semibold text-ui-ink">{title}</div>
        <dl className="space-y-1">
          {rows.map(([term, value]) => (
            <div key={term} className="flex justify-between gap-3">
              <dt className="text-ui-muted">{term}</dt>
              <dd className="text-right text-ui-ink-2">{value}</dd>
            </div>
          ))}
        </dl>
        {footer && <div className="mt-2 pt-2 border-t border-ui-divider text-ui-faint">{footer}</div>}
      </PopoverContent>
    </Popover>
  );
};

const BagIcon = ({ Icon, bag, noun, title, terms }) => {
  const summary = bagSummary(bag, noun);
  if (!summary) return null;
  const typical = terms.source === 'typical';
  const rows = [['Included', bag.quantity === 0 ? 'None' : summary]];
  if (bag.notes) rows.push(['Notes', bag.notes]);
  return (
    <HoverDetail
      label={`${title}: ${typical ? 'typically ' : ''}${summary.toLowerCase()}`}
      title={`${title}${typical ? ' · typical' : ''}`}
      rows={rows}
      footer={sourceLine(terms)}
      iconClassName={`${bag.quantity === 0 ? 'text-ui-faint' : 'text-ui-muted'} ${typical ? 'underline decoration-dashed underline-offset-4' : ''}`}
    >
      <Icon className="w-4 h-4" aria-hidden="true" />
      {bag.quantity > 0 && <span>{bag.quantity}</span>}
    </HoverDetail>
  );
};

const FareTicketIcon = ({ terms }) => {
  // Typical allowances are bags only: fees are never estimated.
  if (terms.source === 'typical') return null;
  const state = ticketState(terms);
  if (!state) return null;
  const Icon = TICKET_ICONS[state];
  const change = changeSummary(terms.change);
  const cancel = cancelSummary(terms.cancel);
  const rows = [];
  if (change) rows.push(['Change', terms.change.notes ? `${change}, ${terms.change.notes}` : change]);
  if (cancel) rows.push(['Cancel', terms.cancel.notes ? `${cancel}, ${terms.cancel.notes}` : cancel]);
  if (terms.fare_basis_code) rows.push(['Fare basis', terms.fare_basis_code]);
  return (
    <HoverDetail
      label={['Fare terms', terms.fare_brand, change && `change ${change.toLowerCase()}`, cancel && cancel.toLowerCase()]
        .filter(Boolean).join(', ')}
      title={terms.fare_brand ? `Fare terms · ${terms.fare_brand}` : 'Fare terms'}
      rows={rows}
      footer={sourceLine(terms)}
      iconClassName={state === 'locked' ? 'text-ui-amber-fg' : 'text-ui-muted'}
    >
      <Icon className="w-4 h-4" aria-hidden="true" />
    </HoverDetail>
  );
};

const FareTermsIcons = ({ terms, showTerms = true }) => {
  if (!terms) return null;
  const items = [
    <BagIcon key="carry_on" Icon={Luggage} bag={terms.carry_on} noun="carry-on bag" title="Carry-on" terms={terms} />,
    <BagIcon key="checked" Icon={BriefcaseConveyorBelt} bag={terms.checked} noun="checked bag" title="Checked bags" terms={terms} />,
    showTerms && <FareTicketIcon key="terms" terms={terms} />,
  ];
  const hasTicket = showTerms && terms.source !== 'typical' && ticketState(terms);
  if (!bagSummary(terms.carry_on, '') && !bagSummary(terms.checked, '') && !hasTicket) return null;
  return <div className="flex items-center gap-0.5">{items}</div>;
};

export default FareTermsIcons;
